import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../../store';
import { productsAPI } from '../../utils/api';

const UnmatchedItemCard = ({ item, idx, unmatchedOptions, loadingOptions, handleAddFromDropdown, removeUnmatched }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const options = query.trim() ? results : (unmatchedOptions[idx] || []);

  const handleSearch = async (val) => {
    setQuery(val);
    if (!val.trim()) {
      setResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const words = val.split(' ').map(w => {
        let word = w.toLowerCase();
        if (word.endsWith('ies') && word.length > 4) return word.slice(0, -3) + 'y';
        if (word.endsWith('es') && word.length > 4) return word.slice(0, -2);
        if (word.endsWith('s') && word.length > 3 && !word.endsWith('ss')) return word.slice(0, -1);
        return word;
      });
      const searchTerms = words.join(' ');
      const res = await productsAPI.getAll({ search: searchTerms || val, limit: 10 });
      setResults(res.data.products || res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', flexDirection: 'column', padding: '1rem',
      borderBottom: '1px solid #fef3c7'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, color: '#92400e', fontSize: '0.95rem' }}>{item.name}</div>
          <div style={{ fontSize: '0.8rem', color: '#b45309' }}>Qty: {item.quantity} {item.optional ? '(Optional)' : ''}</div>
        </div>
        <button onClick={() => removeUnmatched(idx)} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '1.2rem', padding: '0 0.35rem' }}>✕</button>
      </div>
      
      <div style={{ marginTop: '0.75rem', position: 'relative' }}>
        {loadingOptions && !query ? (
          <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Loading options...</div>
        ) : (
          <div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>🔍</span>
              <input 
                type="text"
                value={query}
                onChange={e => handleSearch(e.target.value)}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                placeholder="Search for a replacement..."
                style={{
                  width: '100%',
                  padding: '0.6rem 0.6rem 0.6rem 2rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: '#fff',
                  fontSize: '0.9rem',
                  color: '#1f2937',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {isOpen && (
              <div style={{ 
                position: 'absolute', top: '100%', left: 0, right: 0, 
                background: '#fff', border: '1px solid #d1d5db', borderTop: 'none', 
                borderRadius: '0 0 6px 6px', maxHeight: 220, overflowY: 'auto', 
                zIndex: 50, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' 
              }}>
                {isSearching ? (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>Searching...</div>
                ) : options.length > 0 ? (
                  options.map(prod => (
                    <div 
                      key={prod.id} 
                      onClick={() => handleAddFromDropdown(prod, idx)}
                      style={{ 
                        padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', 
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem' 
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                    >
                      <img src={prod.image_data || prod.image_url} alt={prod.name} style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, background: '#e5e7eb' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{prod.name}</div>
                        <div style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 600 }}>${Number(prod.price).toFixed(2)}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>No products found. Try a different term.</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomKitBuilderPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const addCustomKit = useCartStore(state => state.addCustomKit);
  
  // Student Info State
  const [studentName, setStudentName] = useState('');
  const [grade, setGrade] = useState('');
  
  // Item States
  const [matchedItems, setMatchedItems] = useState([]);
  const [unmatchedItems, setUnmatchedItems] = useState([]);
  const [optionalItems, setOptionalItems] = useState([]);

  // Unmatched Options State
  const [unmatchedOptions, setUnmatchedOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    const rawData = sessionStorage.getItem('customKitData');
    if (!rawData) {
      navigate('/');
      return;
    }
    
    try {
      const parsedData = JSON.parse(rawData);
      
      // Separate matched items by optional/required
      const requiredMatched = [];
      const optionalMatched = [];
      
      parsedData.matched_products?.forEach(item => {
        if (item.optional) {
          optionalMatched.push({ ...item, selected: false }); // Optional items start unselected
        } else {
          requiredMatched.push(item);
        }
      });
      
      setMatchedItems(requiredMatched);
      setOptionalItems(optionalMatched);
      setUnmatchedItems(parsedData.unmatched_items || []);
      
      setData(parsedData);

      // Fetch options for unmatched
      if (parsedData.unmatched_items && parsedData.unmatched_items.length > 0) {
        fetchUnmatchedOptions(parsedData.unmatched_items);
      } else {
        setLoadingOptions(false);
      }
    } catch (e) {
      console.error(e);
      navigate('/');
    }
  }, [navigate]);

  const fetchUnmatchedOptions = async (items) => {
    setLoadingOptions(true);
    const optionsMap = {};
    for (let i = 0; i < items.length; i++) {
      let query = items[i].name;
      const words = query.split(' ').map(w => {
        let word = w.toLowerCase();
        if (word.endsWith('ies') && word.length > 4) return word.slice(0, -3) + 'y';
        if (word.endsWith('es') && word.length > 4) return word.slice(0, -2);
        if (word.endsWith('s') && word.length > 3 && !word.endsWith('ss')) return word.slice(0, -1);
        return word;
      });
      const searchTerms = words.filter(w => w.length > 2).slice(0, 2).join(' ');
      
      try {
        const res = await productsAPI.getAll({ search: searchTerms || query, limit: 10 });
        optionsMap[i] = res.data.products || res.data || [];
      } catch (err) {
        optionsMap[i] = [];
      }
    }
    setUnmatchedOptions(optionsMap);
    setLoadingOptions(false);
  };

  const updateQty = (index, delta, type) => {
    if (type === 'matched') {
      const newItems = [...matchedItems];
      const newQty = Math.max(1, newItems[index].quantity + delta);
      newItems[index].quantity = newQty;
      setMatchedItems(newItems);
    } else if (type === 'optional') {
      const newItems = [...optionalItems];
      const newQty = Math.max(1, newItems[index].quantity + delta);
      newItems[index].quantity = newQty;
      setOptionalItems(newItems);
    }
  };

  const toggleOptional = (index) => {
    const newItems = [...optionalItems];
    newItems[index].selected = !newItems[index].selected;
    setOptionalItems(newItems);
  };

  const removeUnmatched = (index) => {
    const newItems = [...unmatchedItems];
    newItems.splice(index, 1);
    setUnmatchedItems(newItems);
  };

  const removeMatched = (index) => {
    const newItems = [...matchedItems];
    newItems.splice(index, 1);
    setMatchedItems(newItems);
  };

  const handleAddFromDropdown = (product, idx) => {
    const unmatchedItem = unmatchedItems[idx];
    
    const newMatchedItem = {
      product: product,
      quantity: unmatchedItem.quantity || 1,
      original_request: unmatchedItem.name,
      optional: unmatchedItem.optional || false
    };
    
    if (newMatchedItem.optional) {
      setOptionalItems([...optionalItems, { ...newMatchedItem, selected: true }]);
    } else {
      setMatchedItems([...matchedItems, newMatchedItem]);
    }
    
    removeUnmatched(idx);
  };

  const calculateTotal = () => {
    let total = 0;
    matchedItems.forEach(item => {
      total += item.product.price * item.quantity;
    });
    optionalItems.forEach(item => {
      if (item.selected) {
        total += item.product.price * item.quantity;
      }
    });
    return total;
  };

  const handleAddToCart = () => {
    if (!studentName.trim() || !grade.trim()) {
      alert("Please provide Student Name and Grade to continue.");
      return;
    }

    const customKit = {
      isCustomKit: true,
      studentName,
      grade,
      items: [
        ...matchedItems,
        ...optionalItems.filter(item => item.selected)
      ],
      totalPrice: calculateTotal(),
      id: 'custom_kit_' + Date.now(),
      name: `Custom Kit for ${studentName} (${grade})`
    };

    addCustomKit(customKit);
    sessionStorage.removeItem('customKitData');
    navigate('/cart');
  };

  if (!data) return null;

  return (
    <div style={{ background: '#f9fafb', minHeight: 'calc(100vh - 60px)', padding: '2rem 1rem' }}>
      <div className="container" style={{ maxWidth: 800 }}>
        
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', marginBottom: '1rem', fontWeight: 600 }}>
            ← Back
          </button>
          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#111827', margin: '0 0 0.5rem 0' }}>Your Custom Kit is Ready! ✨</h1>
          <p style={{ color: '#4b5563', fontSize: '1rem' }}>
            We've matched your list to our catalog. Review the items below, adjust quantities, and add student details.
          </p>
        </div>

        {/* Missing Items Alert */}
        {unmatchedItems.length > 0 && (
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '1.25rem', marginBottom: '2rem' }}>
            <h3 style={{ margin: '0 0 0.75rem 0', color: '#92400e', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>⚠️</span> Some items were not found in our catalog
            </h3>
            <div style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #fef3c7' }}>
              {unmatchedItems.map((item, idx) => (
                <UnmatchedItemCard 
                  key={idx}
                  item={item} 
                  idx={idx} 
                  unmatchedOptions={unmatchedOptions}
                  loadingOptions={loadingOptions}
                  handleAddFromDropdown={handleAddFromDropdown}
                  removeUnmatched={removeUnmatched}
                />
              ))}
            </div>
          </div>
        )}

        {/* Student Info */}
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937' }}>Student Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4b5563', marginBottom: '0.4rem' }}>Student Name *</label>
              <input 
                type="text" 
                value={studentName} 
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="e.g. John Doe"
                style={{ width: '100%', padding: '0.75rem', borderRadius: 8, border: '1px solid #d1d5db', outline: 'none' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#4b5563', marginBottom: '0.4rem' }}>Grade *</label>
              <input 
                type="text" 
                value={grade} 
                onChange={(e) => setGrade(e.target.value)}
                placeholder="e.g. 5th Grade"
                style={{ width: '100%', padding: '0.75rem', borderRadius: 8, border: '1px solid #d1d5db', outline: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Matched Required Items */}
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937', display: 'flex', justifyContent: 'space-between' }}>
            <span>Required Items</span>
            <span style={{ color: '#4b5563', fontSize: '0.9rem', fontWeight: 400 }}>{matchedItems.length} items</span>
          </h3>
          
          <div style={{ borderTop: '1px solid #e5e7eb' }}>
            {matchedItems.map((item, idx) => (
              <div key={idx} style={{ 
                display: 'flex', gap: '1rem', padding: '1.25rem 0',
                borderBottom: '1px solid #e5e7eb'
              }}>
                {/* Image */}
                <div style={{ flexShrink: 0 }}>
                  {(item.product.image_data || item.product.image_url) ? (
                    <img src={item.product.image_data || item.product.image_url} alt={item.product.name} style={{ width: 80, height: 80, objectFit: 'contain', background: '#fff' }} />
                  ) : (
                    <div style={{ width: 80, height: 80, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>📦</div>
                  )}
                </div>
                
                {/* Content */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
                  <div style={{ fontWeight: 400, color: '#1a365d', fontSize: '1.05rem', lineHeight: '1.3' }}>
                    {item.product.name}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Matched from: "{item.original_request}"</div>
                  
                  {/* Qty Control (Rectangular group) */}
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.25rem' }}>
                    <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden' }}>
                      <button onClick={() => updateQty(idx, -1, 'matched')} style={{ width: 32, height: 32, background: '#f1f5f9', border: 'none', borderRight: '1px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a', fontWeight: 'bold' }}>-</button>
                      <div style={{ width: 40, height: 32, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.95rem' }}>{item.quantity}</div>
                      <button onClick={() => updateQty(idx, 1, 'matched')} style={{ width: 32, height: 32, background: '#f1f5f9', border: 'none', borderLeft: '1px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a', fontWeight: 'bold' }}>+</button>
                    </div>
                  </div>
                  
                  {/* Trash button */}
                  <div style={{ marginTop: '0.25rem' }}>
                    <button onClick={() => removeMatched(idx)} style={{ width: 32, height: 32, background: '#f1f5f9', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
            
            {matchedItems.length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No required items found.</div>
            )}
          </div>
        </div>

        {/* Optional Items */}
        {optionalItems.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#1f2937' }}>Optional / Wishlist Items</h3>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>Select the ones you'd like to add to your kit.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {optionalItems.map((item, idx) => (
                <div key={idx} style={{ 
                  display: 'flex', gap: '1rem', padding: '1.25rem 1rem',
                  border: `2px solid ${item.selected ? '#6366f1' : '#e5e7eb'}`, borderRadius: 12,
                  background: item.selected ? '#eff6ff' : '#fff', transition: 'all 0.2s'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '0.5rem' }}>
                    <input 
                      type="checkbox" 
                      checked={item.selected} 
                      onChange={() => toggleOptional(idx)} 
                      style={{ width: 20, height: 20, cursor: 'pointer' }}
                    />
                  </div>

                  {/* Image */}
                  <div style={{ flexShrink: 0 }}>
                    {(item.product.image_data || item.product.image_url) ? (
                      <img src={item.product.image_data || item.product.image_url} alt={item.product.name} style={{ width: 80, height: 80, objectFit: 'contain', background: 'transparent' }} />
                    ) : (
                      <div style={{ width: 80, height: 80, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>📦</div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
                    <div style={{ fontWeight: 400, color: '#1a365d', fontSize: '1.05rem', lineHeight: '1.3' }}>
                      {item.product.name}
                    </div>
                    
                    {item.selected && (
                      <>
                        {/* Qty Control (Rectangular group) */}
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.25rem' }}>
                          <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '4px', overflow: 'hidden' }}>
                            <button onClick={() => updateQty(idx, -1, 'optional')} style={{ width: 32, height: 32, background: '#f1f5f9', border: 'none', borderRight: '1px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a', fontWeight: 'bold' }}>-</button>
                            <div style={{ width: 40, height: 32, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '0.95rem' }}>{item.quantity}</div>
                            <button onClick={() => updateQty(idx, 1, 'optional')} style={{ width: 32, height: 32, background: '#f1f5f9', border: 'none', borderLeft: '1px solid #d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0f172a', fontWeight: 'bold' }}>+</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Footer */}
        <div style={{ position: 'sticky', bottom: 20, background: '#fff', borderRadius: 16, padding: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb' }}>
          <div>
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Total Kit Price</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111827' }}>${calculateTotal().toFixed(2)}</div>
          </div>
          <button 
            onClick={handleAddToCart}
            style={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', 
              color: '#fff', border: 'none', padding: '0.85rem 2rem', borderRadius: 999,
              fontSize: '1.05rem', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)'
            }}
          >
            Add Kit to Cart 🎒
          </button>
        </div>

      </div>
    </div>
  );
};

export default CustomKitBuilderPage;
